import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Edit, PersonAdd } from '@mui/icons-material'
import { IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material'

import { Analyst } from '../../../../../types/Analyst'
import { useGetIntercomAdminsQuery, useGetInternalAnalystsQuery, useSaveAnalystMutation } from '../../../../api/analysts'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { AnalystEditDialog } from '../../../analysts/components/AnalystEditDialog/AnalystEditDialog'
import { analystFieldLabelKeys, getUnstoredAnalystFields } from '../../../analysts/helpers/analystHelpers'
import { AnalystMutation } from '../../../analysts/types/AnalystMutation'

type AnalystPickerProps = {
  value: string
  onChange: (analystId: string) => void
  disabled?: boolean
}

type AnalystDialogState = { open: boolean; analyst?: Analyst }

// Analyst select with "edit the selected analyst" and "create a new analyst"; a created analyst is selected.
export const AnalystPicker: FC<AnalystPickerProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation()
  const { run, notifyError, notifySuccess } = useMutationFeedback()
  const [dialog, setDialog] = useState<AnalystDialogState>({ open: false })
  const analystsQuery = useGetInternalAnalystsQuery()
  const intercomAdminsQuery = useGetIntercomAdminsQuery(undefined, { skip: !dialog.open })
  const [saveAnalyst, { isLoading: isSaving }] = useSaveAnalystMutation()
  const analysts = analystsQuery.data ?? []
  const selected = analysts.find(({ id }) => id === value)

  const handleSave = async (analyst: AnalystMutation) => {
    const saved = await run(saveAnalyst(analyst).unwrap(), { error: t('internal-analysts:analysts_save_failed') })
    if (!saved.ok) return
    const stored = saved.result
    const unstored = getUnstoredAnalystFields(analyst, stored)
    if (unstored.length) {
      notifyError(t('internal-analysts:analysts_not_stored', { fields: unstored.map((field) => t(analystFieldLabelKeys[field])).join(', ') }))
      return
    }
    notifySuccess(t('internal-analysts:analysts_saved', { name: stored.name }))
    onChange(stored.id)
    setDialog({ open: false })
  }

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField
        select
        fullWidth
        size="small"
        label={t('internal-analysts:review_analyst')}
        value={analystsQuery.isLoading ? '' : value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || analystsQuery.isLoading}
        error={analystsQuery.isError}
        helperText={analystsQuery.isError ? t('internal-analysts:analysts_load_failed') : undefined}
      >
        <MenuItem value="">{t('internal-analysts:review_analyst_none')}</MenuItem>
        {analysts.map((analyst) => (
          <MenuItem key={analyst.id} value={analyst.id}>
            {analyst.inactive ? t('internal-analysts:review_analyst_inactive', { name: analyst.name }) : analyst.name}
          </MenuItem>
        ))}
        {value && !selected && !analystsQuery.isLoading && <MenuItem value={value}>{value}</MenuItem>}
      </TextField>
      <Tooltip title={t('internal-analysts:games_analyst_edit')}>
        <span>
          <IconButton
            aria-label={t('internal-analysts:games_analyst_edit')}
            disabled={disabled || !selected}
            onClick={() => setDialog({ open: true, analyst: selected })}
          >
            <Edit />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('internal-analysts:games_analyst_create')}>
        <span>
          <IconButton aria-label={t('internal-analysts:games_analyst_create')} disabled={disabled} onClick={() => setDialog({ open: true })}>
            <PersonAdd />
          </IconButton>
        </span>
      </Tooltip>
      <AnalystEditDialog
        open={dialog.open}
        analyst={dialog.analyst}
        intercomAdmins={intercomAdminsQuery.data}
        isLoadingIntercomAdmins={intercomAdminsQuery.isLoading}
        isSaving={isSaving}
        onClose={() => setDialog({ open: false })}
        onSave={handleSave}
      />
    </Stack>
  )
}
